import React from 'react';
import { cloneDeep } from 'lodash';

import { Overmind } from 'overmind';
import { camelCaseToDash } from '../common/utils';
import { getClass } from '../../../utils/component-helpers';

import { dateService } from '../../../services/date';
import { session } from '../../../services/session';

import AuctionOverride from './auction-override';
import BasicDropdown from '../dropdowns/basic-dropdown';

import linkoutIcon from '../../../images/icons/black-linkout.png';

import './auction-participant.scss';
import { withOvermind } from '../../../containers/overmind/hocs/withOvermind';
import { Context } from '../../../containers/overmind';

type MyProps = {
  overmind: any;
  product: any;
  datetimeUpdated: any;
  auctionClassName: any;
  updateProduct: any;
  productIndex: any;
  disabled?: any;
  winnerConfig: any;
  productIdxWithError: any;
  setError: any;
};
type MyState = {
  stockStatusLabel: any;
  datetimeImported: any;
  auctionStatusLabel: any;
  auctionStatusFields: any;
  auctionClassName: any;
  overridesToggled: any;
  overridesOpen: any;
};

class AuctionParticipant extends React.Component<MyProps, MyState> {
  DateService: any;

  Session: any;

  detailMap: any;

  constructor(props) {
    super(props);

    const {
      getDefaultStockFields,
      getDefaultAuctionFields,
      getLabel,
    } = this.props.overmind.actions.smartLink;

    this.DateService = dateService;
    this.Session = session;

    // Used to set this.state.stockStatusLabel
    const stockFields = getDefaultStockFields({
      isInStock: this.props.product.is_in_stock,
    });

    // Used to set both this.state.auctionStatusLabel && this.state.auctionStatusFields
    const auctionFields = getDefaultAuctionFields(
      this.props.product.auction_config.participate_in_auction.value,
    );

    this.state = {
      datetimeImported: this.DateService.formatDate(
        this.props.datetimeUpdated,
        'MM/DD/YY, hh:mm A',
      ),

      // Stock Specific
      stockStatusLabel: getLabel({ items: stockFields }),

      // Auction Specific
      auctionStatusLabel: getLabel({ items: auctionFields }),
      auctionStatusFields: auctionFields,
      auctionClassName: this.props.auctionClassName,

      // Override
      overridesToggled: {
        price: false,
        stock: false,
      },
      overridesOpen: {
        price: false,
        stock: false,
      },
    };

    this.detailMap = {
      retailerPrice: {
        label: `Retailer's Price`,
        value: this.props.product.item_data.sale_price,
      },
      stockStatus: {
        label: `Retailer's Stock Status`,
        value: this.state.stockStatusLabel,
      },
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.auctionClassName !== this.props.auctionClassName) {
      this.setState({ auctionClassName: this.props.auctionClassName });
    }
  }

  toggleOverride(override) {
    this.setState({
      overridesToggled: {
        ...this.state.overridesToggled,
        [override]: !this.state.overridesToggled[override],
      },
    });
  }

  toggleOverrideOpen(override) {
    this.setState({
      overridesOpen: {
        ...this.state.overridesOpen,
        [override]: !this.state.overridesOpen[override],
      },
    });
  }

  // Dropdown helpers
  selectAuctionStatus(index) {
    // Need to make deep copy to prevent mutating state
    const auctionStatuses = cloneDeep(this.state.auctionStatusFields);

    // Reset all items
    auctionStatuses.map(status => (status.selected = false));
    auctionStatuses[index].selected = true;

    this.setState(
      {
        auctionStatusFields: auctionStatuses,
        auctionStatusLabel: auctionStatuses[index].label,
        auctionClassName: auctionStatuses[index].label.toLowerCase(),
      },
      () => {
        // Update product
        const product = cloneDeep(this.props.product);
        product.auction_config.participate_in_auction.value = auctionStatuses[index].value;

        this.props.updateProduct(product, this.props.productIndex, this.state.auctionClassName);
      },
    );
  }

  render() {
    const overlay = this.props.disabled ? <div className="overlay" /> : null;

    const winnerInfo = this.props.winnerConfig.winner ? (
      <p className="winner">
        {this.props.winnerConfig.multipleWinners ? 'Tied For' : 'Current'} Auction Winner
      </p>
    ) : null;

    const datetimeFeedImported = (
      <p className="datetime-imported">
        {`${session.isAdmin ? `Retailer's feed imported ${this.state.datetimeImported}` : ''}`}
      </p>
    );

    const productDetails = Object.keys(this.detailMap).map((key, idx) => {
      const className = camelCaseToDash(key);
      const keyValue = this.detailMap[key];
      const opened =
        key === 'retailerPrice' ? this.state.overridesOpen.price : this.state.overridesOpen.stock;
      const grayed = opened ? 'gray' : '';

      return (
        <div key={key} className="content-container">
          <div className="detail-container">
            <div className={`prod-detail ${className}`}>
              <div className={`label ${className}`}>{this.detailMap[key].label}</div>
              <div
                className={getClass(`value-container ${className} ${grayed}`, {
                  'active-override': this.state.overridesToggled[key],
                })}
              >
                <div className={`value ${className}`}>
                  {className.includes('price') ? '$' : ''}
                  {keyValue.value}
                </div>
              </div>
            </div>
          </div>

          <div className="override-options">
            <AuctionOverride
              type={key}
              toggleOverride={this.toggleOverride.bind(this)}
              toggleOverrideOpen={this.toggleOverrideOpen.bind(this)}
              product={this.props.product}
              productIndex={this.props.productIndex}
              productIdxWithError={this.props.productIdxWithError}
              updateProduct={this.props.updateProduct}
              setError={this.props.setError}
              overmind={this.props.overmind}
            />
          </div>
        </div>
      );
    });

    return (
      <div className={`auction-value ${this.state.auctionClassName}`}>
        {overlay}
        <div className="product-information">
          <div className="product-header">
            <p className="merchant-name">{this.props.product.merchant.full_name}</p>
            <a href={this.props.product.store_page_url} target="_blank" rel="noopener noreferrer">
              <img
                src={linkoutIcon}
                alt="Open product"
                className={`link-out ${camelCaseToDash(this.props.product.merchant.slug)}`}
              />
            </a>
            {winnerInfo}
            {datetimeFeedImported}
          </div>

          {productDetails}

          <div className="prod-detail auction-status">
            <div className="label auction-status">Auction Status</div>
            <div className="value-container auction-status">
              <BasicDropdown
                items={this.props.overmind.state.smartLink.auctionStatusFields}
                selectItem={this.selectAuctionStatus.bind(this)}
                dropdownLabel={this.state.auctionStatusLabel}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withOvermind(AuctionParticipant);
